import { useTranslation } from 'react-i18next';
import {
  ANNOUNCEMENT_FONTS as F,
  ANNOUNCEMENT_PALETTE as P,
  announcementButtonBase,
  formatAnnouncementDate,
  type AnnouncementCardProps,
} from '../announcement-style';

const STYLES = `
@keyframes announcementLetterIn {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
.announcement-letter { padding: 26px 32px 28px; }
.announcement-letter h2 { font-size: clamp(1.5rem, 5vw, 1.95rem); }
@container announcement (max-width: 700px) {
  .announcement-letter { padding: 20px 20px 22px; }
}
`;

/**
 * "Carta da escola" — cream stationery with a double red rule, a small round
 * brand mark, the date, a salutation, a serif headline, the body as a letter
 * and the team's signature. Intimate, woman-to-woman; the opposite of a
 * system alert.
 */
export function SchoolLetterCard(props: AnnouncementCardProps) {
  const { t } = useTranslation('p2e');

  return (
    <article
      role="dialog"
      aria-modal="true"
      aria-label={props.title}
      className="announcement-letter"
      onClick={(event) => event.stopPropagation()}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 520,
        maxHeight: '90vh',
        overflowY: 'auto',
        background: P.cream,
        color: P.darkText,
        border: `1px solid ${P.lightBorder}`,
        borderTop: `4px double ${P.maxRed}`,
        borderRadius: 2,
        boxShadow: '0 24px 60px rgba(0,0,0,.42)',
        animation: props.reducedMotion ? undefined : 'announcementLetterIn .35s ease both',
      }}
    >
      <style>{STYLES}</style>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 22 }}>
        <div
          aria-hidden="true"
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            border: `1.5px solid ${P.maxRed}`,
            color: P.maxRed,
            display: 'grid',
            placeItems: 'center',
            fontFamily: F.cond,
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: '.06em',
          }}
        >
          E2E
        </div>
        <span
          style={{
            fontFamily: F.cond,
            fontSize: 11,
            letterSpacing: '.26em',
            textTransform: 'uppercase',
            color: P.warmGray,
          }}
        >
          {formatAnnouncementDate(props.date, props.locale, 'long')}
        </span>
      </div>

      <p style={{ fontFamily: F.serif, fontStyle: 'italic', fontSize: 16, color: P.midText, margin: '0 0 8px' }}>
        {t('announcements.salute', { context: props.genderContext })}
      </p>

      <h2
        style={{
          fontFamily: F.serif,
          fontWeight: 400,
          lineHeight: 1.15,
          margin: '0 0 14px',
          textWrap: 'balance',
        }}
      >
        <mark style={{ background: `linear-gradient(transparent 62%, ${P.baby} 62%)`, color: 'inherit', padding: '0 .08em' }}>
          {props.title}
        </mark>
      </h2>

      {props.body && (
        <p
          style={{
            fontFamily: F.serif,
            fontSize: 16,
            lineHeight: 1.7,
            color: P.midText,
            margin: '0 0 18px',
            whiteSpace: 'pre-line',
          }}
        >
          {props.body}
        </p>
      )}

      {props.media && <div style={{ marginBottom: 18 }}>{props.media}</div>}

      <p style={{ margin: '0 0 22px', fontFamily: F.serif, fontStyle: 'italic', color: P.midText, fontSize: 15, lineHeight: 1.5 }}>
        {t('announcements.signoff')}
        <span style={{ display: 'block', color: P.maxRed, fontSize: 18 }}>
          {t('announcements.signature')}
          <span
            aria-hidden="true"
            style={{
              display: 'block',
              width: 110,
              height: 2,
              marginTop: 3,
              background: `linear-gradient(90deg, ${P.maxRed}, transparent)`,
              borderRadius: 2,
            }}
          />
        </span>
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <button
          ref={props.primaryRef}
          type="button"
          disabled={props.primaryDisabled}
          onClick={props.onPrimary}
          style={{
            ...announcementButtonBase,
            background: P.maxRed,
            color: P.white,
            opacity: props.primaryDisabled ? 0.45 : 1,
            cursor: props.primaryDisabled ? 'default' : 'pointer',
          }}
        >
          {props.primaryLabel}
        </button>
        {props.skippable && (
          <button
            type="button"
            onClick={props.onSkip}
            style={{
              fontFamily: F.cond,
              fontSize: 11,
              letterSpacing: '.16em',
              textTransform: 'uppercase',
              color: P.warmGray,
              textDecoration: 'underline',
              textUnderlineOffset: 4,
              background: 'none',
              border: 0,
              padding: '0 6px',
              minHeight: 44,
              cursor: 'pointer',
            }}
          >
            {t('announcements.readLater')}
          </button>
        )}
      </div>

      {props.gatedHint && (
        <p style={{ fontFamily: F.serif, fontStyle: 'italic', fontSize: 13, color: P.midText, margin: '10px 0 0' }}>
          {props.gatedHint}
        </p>
      )}
    </article>
  );
}
